import { lazy, type ReactElement, Suspense } from 'react'
import { GameHostRoute } from '../games/route.js'
import {
  Dersler,
  Gozlem,
  Onboarding,
  Panel,
  ParentGate,
  Plus,
  ProfilePick,
  Reminders,
  Settings,
  Share,
  Takvim,
} from '../parent/index.js'
import { BadgesRoute, ShopRoute, WardrobeRoute, YuvaRoute } from '../pet/index.js'
import { useChildStore } from '../state/store.js'
import { NotFoundScreen } from './NotFoundScreen.js'
import { ScreenState } from './ScreenState.js'
import { SplashScreen } from './Splash.js'

const ChestScreen = lazy(() =>
  import('../learn/ChestScreen.js').then((module) => ({ default: module.ChestScreen })),
)

const LessonScreen = lazy(() =>
  import('../learn/LessonScreen.js').then((module) => ({ default: module.LessonScreen })),
)

const MapScreen = lazy(() =>
  import('../learn/MapScreen.js').then((module) => ({ default: module.MapScreen })),
)

const QuizScreen = lazy(() =>
  import('../learn/QuizScreen.js').then((module) => ({ default: module.QuizScreen })),
)

const RecapScreen = lazy(() =>
  import('../learn/RecapScreen.js').then((module) => ({ default: module.RecapScreen })),
)

const RecapQuizScreen = lazy(() =>
  import('../learn/RecapQuizScreen.js').then((module) => ({ default: module.RecapQuizScreen })),
)

/**
 * WP-A. The ritual is the only part of the app that loads MediaPipe, so the four screens are
 * their own lazy chunk: ARCHITECTURE §9 keeps the app shell under 350 kB gzip and PERF-05
 * wants the detector on `/firca/*` only.
 */
const HazirlikScreen = lazy(() =>
  import('../brush/index.js').then((module) => ({ default: module.HazirlikScreen })),
)
const AynaScreen = lazy(() =>
  import('../brush/index.js').then((module) => ({ default: module.AynaScreen })),
)
const OturumScreen = lazy(() =>
  import('../brush/index.js').then((module) => ({ default: module.OturumScreen })),
)
const SonucScreen = lazy(() =>
  import('../brush/index.js').then((module) => ({ default: module.SonucScreen })),
)

function lazily(screen: ReactElement): ReactElement {
  return (
    <Suspense
      fallback={
        <ScreenState status="loading" onRetry={() => window.location.reload()}>
          {null}
        </ScreenState>
      }
    >
      {screen}
    </Suspense>
  )
}

/**
 * `/veli/plus` while `flags.plus` is off (QA-064). The paywall itself renders nothing in the
 * pilot, which painted a blank page with no way out for a parent arriving on a stale link, so
 * the route behaves exactly as if it were not in the table until the flag turns on.
 */
function PlusRoute(): ReactElement {
  const plus = useChildStore((store) => store.flags.plus)
  return plus ? <Plus /> : <NotFoundScreen />
}

/**
 * The closed route set from ARCHITECTURE §3, in one table. Every route names the element that
 * answers it; a path with no screen of its own answers with the not found screen, never with a
 * developer card (QA-063).
 */

export type RouteSpec = {
  /** Path relative to the router root, exactly as ARCHITECTURE §3 writes it. */
  path: string
  /** Child routes need an active profile (CONTRACTS §7). */
  guard?: 'child' | 'parent' | 'none'
  element: ReactElement
}

export const CHILD_ROUTES: RouteSpec[] = [
  { path: '/', guard: 'none', element: <SplashScreen /> },
  { path: '/profil', guard: 'none', element: <ProfilePick /> },
  { path: '/yuva', guard: 'child', element: <YuvaRoute /> },
  {
    path: '/firca/hazirlik',
    guard: 'child',
    element: lazily(<HazirlikScreen />),
  },
  {
    path: '/firca/ayna',
    guard: 'child',
    element: lazily(<AynaScreen />),
  },
  {
    path: '/firca/oturum',
    guard: 'child',
    element: lazily(<OturumScreen />),
  },
  {
    path: '/firca/sonuc',
    guard: 'child',
    element: lazily(<SonucScreen />),
  },
  { path: '/harita', guard: 'child', element: lazily(<MapScreen />) },
  {
    path: '/ders/:lessonId',
    guard: 'child',
    element: lazily(<LessonScreen />),
  },
  {
    path: '/soru/:lessonId',
    guard: 'child',
    element: lazily(<QuizScreen />),
  },
  {
    path: '/sandik/:lessonId',
    guard: 'child',
    element: lazily(<ChestScreen />),
  },
  {
    path: '/hatirlatma',
    guard: 'child',
    element: lazily(<RecapScreen />),
  },
  {
    path: '/hatirlatma/soru',
    guard: 'child',
    element: lazily(<RecapQuizScreen />),
  },
  {
    path: '/oyun/:gameId',
    guard: 'child',
    element: <GameHostRoute />,
  },
  { path: '/dukkan', guard: 'child', element: <ShopRoute /> },
  { path: '/dolap', guard: 'child', element: <WardrobeRoute /> },
  { path: '/rozetler', guard: 'child', element: <BadgesRoute /> },
]

export const PARENT_ROUTES: RouteSpec[] = [
  { path: '/veli', guard: 'none', element: <ParentGate /> },
  {
    path: '/veli/onboarding/*',
    guard: 'none',
    element: <Onboarding />,
  },
  { path: '/veli/panel', guard: 'parent', element: <Panel /> },
  {
    path: '/veli/takvim/:childId',
    guard: 'parent',
    element: <Takvim />,
  },
  {
    path: '/veli/dersler/:childId',
    guard: 'parent',
    element: <Dersler />,
  },
  {
    path: '/veli/gozlem/:childId',
    guard: 'parent',
    element: <Gozlem />,
  },
  {
    path: '/veli/hatirlatma',
    guard: 'parent',
    element: <Reminders />,
  },
  { path: '/veli/paylas', guard: 'parent', element: <Share /> },
  {
    path: '/veli/ayarlar',
    guard: 'parent',
    element: <Settings />,
  },
  { path: '/veli/plus', guard: 'parent', element: <PlusRoute /> },
]

/**
 * Server rendered in production (ARCHITECTURE §3), never linked from a child route. The two
 * paths stay in the table so the route set is still the documented one, but inside the app
 * they are nothing: nginx answers them, the bundle answers with the not found screen.
 */
export const PUBLIC_ROUTES: RouteSpec[] = [
  { path: '/hekim', guard: 'none', element: <NotFoundScreen /> },
  { path: '/arastirma', guard: 'none', element: <NotFoundScreen /> },
]

export const ALL_ROUTES = [...CHILD_ROUTES, ...PARENT_ROUTES, ...PUBLIC_ROUTES]
